import React, { useState } from "react";
import { Flex, ModalV2, Select, Textarea, Text } from "@jsq/ds/core";
import { SPACING } from "#utils/spacing";
import { useGpUsers } from "../../../hooks/useGpUsers";
import { SubtaskItemType } from "../../../types";
import * as styles from "../styles_vanilla.css";
import type { TaskStepDetail, SubtaskItem } from "../../../types";

interface ConfirmSubmitModalProps {
  isOpen: boolean;
  task: TaskStepDetail;
  /** Arena id used to fetch the GP user list via `gpUsersForArena`. */
  arenaId?: number;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: (note: string, assigneeId: string) => void;
}

const DOC_SUBTASK_TYPES = new Set<SubtaskItemType>([SubtaskItemType.DOCUMENT_UPLOAD]);

function uploadedDocsFromTask(task: TaskStepDetail): SubtaskItem[] {
  return task.subtasks.filter((s) => DOC_SUBTASK_TYPES.has(s.itemType));
}

function fileNameFromValue(value: string | boolean | undefined): string | null {
  if (typeof value !== "string" || !value) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (Array.isArray(parsed) && typeof parsed[0] === "string") return parsed[0];
  } catch {
    // Ignore parsing errors
  }
  return value;
}

export const ConfirmSubmitModal: React.FC<ConfirmSubmitModalProps> = ({
  isOpen,
  task,
  arenaId,
  loading = false,
  onCancel,
  onConfirm,
}) => {
  const { users: gpUsers, loading: usersLoading } = useGpUsers(arenaId);
  const [assigneeId, setAssigneeId] = useState<string>("");
  const [note, setNote] = useState("");

  // Seed the default assignee to the first user as soon as the BE list arrives.
  // Derived-state-during-render pattern (not useEffect) — same pattern Checker
  // / OnBehalf bars use to react to prop changes without triggering the
  // react-hooks/set-state-in-effect cascade-render flag.
  const [lastSeenUserIds, setLastSeenUserIds] = useState<string>("");
  const userIdsKey = gpUsers.map((u) => u.id).join(",");
  if (userIdsKey !== lastSeenUserIds) {
    setLastSeenUserIds(userIdsKey);
    if (gpUsers.length > 0 && !gpUsers.some((u) => u.id === assigneeId)) {
      setAssigneeId(gpUsers[0].id);
    }
  }

  const docs = uploadedDocsFromTask(task);

  const reset = () => {
    setAssigneeId(gpUsers[0]?.id ?? "");
    setNote("");
  };

  const handleHide = () => {
    reset();
    onCancel();
  };

  const handleSubmit = () => {
    onConfirm(note, assigneeId);
    reset();
  };

  const confirmDisabled = loading || usersLoading || !assigneeId;

  return (
    <ModalV2
      show={isOpen}
      onHide={handleHide}
      title="Confirmation"
      size="medium"
      data-testid="confirm-submit-modal"
    >
      <ModalV2.Body>
        <Flex flexDirection="column" gap={SPACING.LARGE}>
          <div>
            <div className={styles.modalLabel}>Assign to</div>
            <Select
              value={assigneeId}
              onChange={(v) => setAssigneeId(String(v))}
              full
              inModal
              label="Assign to"
              hideLabel
              disabled={usersLoading || gpUsers.length === 0}
              placeholder={usersLoading ? "Loading users…" : "No users available"}
              data-testid="confirm-submit-assignee-select"
            >
              <Select.Section>
                {gpUsers.map((u) => (
                  <Select.Option key={u.id} value={u.id}>
                    {u.displayName}
                  </Select.Option>
                ))}
              </Select.Section>
            </Select>
          </div>

          {docs.length > 0 && (
            <div>
              <div className={styles.modalLabel}>
                Documents and data that will be sent to the client to review
              </div>
              <Flex
                flexDirection="column"
                gap={SPACING.X_SMALL}
                className={styles.modalDocsList}
              >
                {docs.map((d) => {
                  const fileName = fileNameFromValue(d.value);
                  return (
                    <Flex
                      key={d.id}
                      alignItems="center"
                      gap={SPACING.X_SMALL}
                      className={styles.modalDocItem}
                    >
                      <Text variant="regular-sm">{d.label}</Text>
                      {fileName && <Text variant="secondary-sm">{fileName}</Text>}
                    </Flex>
                  );
                })}
              </Flex>
            </div>
          )}

          <div>
            <Textarea
              name="confirm-submit-note"
              label="Note"
              value={note}
              onSetValue={setNote}
              placeholder="This note will be visible in the GP's workspace"
              isFullWidth
              size="medium"
            />
            <div className={styles.modalNoteHint}>Optional</div>
          </div>
        </Flex>
      </ModalV2.Body>
      <ModalV2.Footer>
        <Flex justifyContent="flex-end" gap={SPACING.SMALL} className={styles.modalFooter}>
          <button
            className={styles.actionButton}
            onClick={handleHide}
            disabled={loading}
            data-testid="confirm-submit-modal-cancel"
          >
            Cancel
          </button>
          <button
            className={styles.actionButtonPrimary}
            onClick={handleSubmit}
            disabled={confirmDisabled}
            data-testid="confirm-submit-modal-confirm"
          >
            Continue
          </button>
        </Flex>
      </ModalV2.Footer>
    </ModalV2>
  );
};

export default ConfirmSubmitModal;
